import { useEffect, useState } from "react";
import { GraduationCap, ShieldCheck } from "lucide-react";
import { api, money } from "../lib/api";
import { TIER_META } from "../lib/tiers";

const SOURCES = [
  ["trip_cycle", "Club gifts", "Gifts the club makes from its own operating income"],
  ["donated_trip", "Donated trips", "Voyagers who gave their whole trip away"],
  ["partner_commission", "Partner commission", "A share of partner booking commission"],
  ["member_addon", "Member add-ons", "Optional monthly add-ons, 100% to schools"],
];

const Big = ({ label, value, sub, tid, accent }) => (
  <div className="lux-card p-6">
    <p className="eyebrow !text-slate-500">{label}</p>
    <p data-testid={tid} className={`font-mono-lux text-3xl mt-3 ${accent ? "text-[#38BDF8]" : ""}`}>{money(value)}</p>
    {sub && <p className="text-xs text-slate-500 mt-2">{sub}</p>}
  </div>
);

const SchoolRow = ({ c }) => (
  <div data-testid={`impact-school-${c.id}`} className="grid md:grid-cols-12 gap-3 p-5 items-center">
    <div className="md:col-span-4">
      <p className="font-medium flex items-center gap-1.5">{c.name}{c.verify_status === "verified" && <ShieldCheck className="w-3.5 h-3.5 shrink-0 text-emerald-300" />}</p>
      <p className="text-xs text-slate-500">{[c.city, c.state].filter(Boolean).join(", ")}{c.accreditor ? ` · ${c.accreditor}` : ""}</p>
    </div>
    {SOURCES.map(([k, label]) => (
      <div key={k} className="md:col-span-1 lg:col-span-1"><p className="text-[10px] uppercase text-slate-500 md:hidden">{label}</p><p className="font-mono-lux text-sm">{money(c[k])}</p></div>
    ))}
    <p data-testid={`impact-school-total-${c.id}`} className="md:col-span-4 font-mono-lux text-[#38BDF8] md:text-right">{money(c.total)}</p>
  </div>
);

export default function CharityImpactPage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/charity-impact").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="pt-40 text-center text-slate-500">Loading…</div>;
  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10 pt-32 pb-24" data-testid="impact-page">
      <p className="eyebrow"><GraduationCap className="w-3.5 h-3.5 mr-1.5" />Scholarship impact</p>
      <h1 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl">Every trip funds someone's trade.</h1>
      <p className="text-sm md:text-base text-slate-400 mt-4 max-w-2xl">A live, public total of the scholarships VoyageQueue has funded at accredited trade and tech schools. Money goes to each school's scholarship office, never directly to students, and no member names are shown.</p>
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Big label="Scholarships funded" value={d.totals.total} tid="impact-total" accent />
        {SOURCES.map(([k, label, sub]) => <Big key={k} label={label} value={d.totals[k]} sub={sub} tid={`impact-total-${k}`} />)}
      </div>
      <div className="mt-12 lux-card divide-y divide-white/5" data-testid="impact-school-list">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] uppercase tracking-wider text-slate-500">
          <span className="col-span-4">School</span>{SOURCES.map(([k, l]) => <span key={k} className="col-span-1">{l}</span>)}<span className="col-span-4 text-right">Total</span>
        </div>
        {d.charities.map((c) => <SchoolRow key={c.id} c={c} />)}
        {d.charities.length === 0 && <p className="p-6 text-sm text-slate-500">No approved schools yet.</p>}
      </div>
      <h2 className="text-base md:text-lg mt-14 font-medium">Trips donated to scholarships</h2>
      <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="impact-donated-trips-list">
        {d.donated_trips.map((t, i) => (
          <div key={i} data-testid={`impact-donated-${i}`} className="lux-card p-5">
            <p className="text-sm"><span className="text-[#38BDF8]">{t.alias}</span> gave up <b>{t.vacation_title}</b></p>
            <p className="text-xs text-slate-500 mt-1">{TIER_META[t.tier]?.label || t.tier} · to {t.charity_name} · {money(t.amount)}</p>
          </div>
        ))}
        {d.donated_trips.length === 0 && <p className="text-sm text-slate-500" data-testid="impact-no-donated">No trips donated yet. Voyagers can give their trip's value to trade-school scholarships.</p>}
      </div>
    </div>
  );
}
